import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import CallLogs from './components/CallLogs';
import Outbound from './components/Outbound';
import Users from './components/Users';
import Test from './components/Test';
import FeedbackManagement from './components/FeedbackManagement';
import OrganizationSelector from './components/OrganizationSelector';
import Batches from './components/Batches';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { organizationService } from './utils/api';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" />;
  }
  return children;
};

// Organization protected route component
const OrganizationProtectedRoute = ({ children, allowOrgQueryParam = false }) => {
  const token = localStorage.getItem('token');
  const selectedOrganization = organizationService.getSelectedOrganization();
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  // If allowOrgQueryParam is true, check if org query param exists
  if (!selectedOrganization) {
    if (allowOrgQueryParam) {
      const searchParams = new URLSearchParams(window.location.search);
      const orgId = searchParams.get('org');
      
      // If org query param exists, allow access
      if (orgId) {
        return children;
      }
    }
    
    return <Navigate to="/organizations" />;
  }
  
  return children;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#4361ee', // vibrant blue for primary actions
      light: '#738bff',
      dark: '#2541b2',
    },
    secondary: {
      main: '#f72585', // accent pink from vibrant palette
      light: '#ff64b1',
      dark: '#c00058',
    },
    success: {
      main: '#06d6a0', // bright teal for success indicators
    },
    error: {
      main: '#ef476f', // softer red for errors
    },
    warning: {
      main: '#ffd166', // soft yellow for warnings
    },
    background: {
      default: '#f8f9fc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e2a3a',
      secondary: '#66758c',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      'Segoe UI',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.05)',
    '0px 8px 16px rgba(0, 0, 0, 0.05)',
    '0px 12px 24px rgba(0, 0, 0, 0.05)',
    '0px 16px 32px rgba(0, 0, 0, 0.05)',
    ...Array(19).fill('none'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/organizations" element={
            <ProtectedRoute>
              <OrganizationSelector />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <OrganizationProtectedRoute>
              <Dashboard />
            </OrganizationProtectedRoute>
          } />
          <Route path="/call-logs" element={
            <OrganizationProtectedRoute>
              <CallLogs />
            </OrganizationProtectedRoute>
          } />
          <Route path="/call-logs/:callId" element={
            <OrganizationProtectedRoute allowOrgQueryParam={true}>
              <CallLogs />
            </OrganizationProtectedRoute>
          } />
          <Route path="/outbound" element={
            <OrganizationProtectedRoute>
              <Outbound />
            </OrganizationProtectedRoute>
          } />
          <Route path="/users" element={
            <OrganizationProtectedRoute>
              <Users />
            </OrganizationProtectedRoute>
          } />
          <Route path="/test" element={
            <OrganizationProtectedRoute>
              <Test />
            </OrganizationProtectedRoute>
          } />
          <Route path="/feedback" element={
            <OrganizationProtectedRoute>
              <FeedbackManagement />
            </OrganizationProtectedRoute>
          } />
          <Route path="/batches" element={
            <OrganizationProtectedRoute>
              <Batches />
            </OrganizationProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
      <ToastContainer position="top-right" autoClose={5000} />
    </ThemeProvider>
  );
}

export default App; 